/* eslint-disable react/prop-types */
import { Col, Divider, Row } from 'antd';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { type BmwModuleSpecsFragment } from '../../../../../api/fragments/BmwModuleSpecs';
import {
    CreateBmwModuleDocument,
    type CreateBmwModuleMutation,
    type CreateBmwModuleMutationVariables,
} from '../../../../../api/mutations/createBmwModule';
import { BankIntegrationProvider, type BmwModuleSettings } from '../../../../../api/types';
import BankSelectField from '../../../../../components/fields/BankSelectField';
import DealerSelectField from '../../../../../components/fields/DealerSelectField';
import InputField from '../../../../../components/fields/InputField';
import PasswordField from '../../../../../components/fields/PasswordField';
import SelectField from '../../../../../components/fields/SelectField';
import validators from '../../../../../utilities/validators';
import BmwModulePage, { type BmwModulePageProps } from '../../BmwModulePage';
import useModuleOptions from './shared/useModuleOptions';
import { type ModuleImplementation } from './types';

type BmwModuleImplementation = ModuleImplementation<BmwModuleSpecsFragment, BmwModuleSettings>;

const formValidator = validators.compose(
    validators.requiredNonEmptyString('displayName'),
    validators.requiredString('bankModuleId'),
    validators.requiredString('customerModuleId'),
    validators.requiredString('bankId'),
    validators.requiredString('sdmHost'),
    validators.requiredString('sdmAccessKey'),
    validators.requiredString('sdmSecretKey'),
    validators.requiredString('dealerId'),
    validators.requiredString('vehicleModuleId')
);

const colSpan = { md: 8, xs: 24 };

const Form: BmwModuleImplementation['FormComponent'] = ({ company }) => {
    const { t } = useTranslation(['moduleDetails', 'bmwModuleDetails']);
    const options = useModuleOptions(company);
    const { values } = useFormikContext<BmwModuleSettings>();

    return (
        <>
            <Row gutter={10}>
                <Col {...colSpan}>
                    <InputField
                        {...t('moduleDetails:fields.displayName', { returnObjects: true })}
                        name="displayName"
                        required
                    />
                </Col>
            </Row>
            <Divider>{t('moduleDetails:sections.dependencies')}</Divider>
            <Row gutter={10}>
                <Col md={12} xs={24}>
                    <SelectField
                        {...t('moduleDetails:fields.customerModuleId', { returnObjects: true })}
                        name="customerModuleId"
                        options={options.customers}
                        required
                        showSearch
                    />
                </Col>
                <Col md={12} xs={24}>
                    <SelectField
                        {...t('moduleDetails:fields.vehicleModuleId', { returnObjects: true })}
                        name="vehicleModuleId"
                        options={options.vehicles}
                        required
                        showSearch
                    />
                </Col>
                <Col md={12} xs={24}>
                    <SelectField
                        {...t('moduleDetails:fields.bankModuleId', { returnObjects: true })}
                        name="bankModuleId"
                        options={options.financing}
                        required
                        showSearch
                    />
                </Col>
                <Col md={12} xs={24}>
                    <BankSelectField
                        {...t('moduleDetails:fields.bankId', { returnObjects: true })}
                        bankIntegrationProviders={[BankIntegrationProvider.Email, BankIntegrationProvider.Dbs]}
                        companyId={company.id}
                        disabled={!values.bankModuleId}
                        forceSkipFetch={!values.bankModuleId}
                        moduleId={values.bankModuleId}
                        name="bankId"
                        required
                    />
                </Col>
                <Col {...colSpan}>
                    <DealerSelectField
                        {...t('moduleDetails:fields.dealer', { returnObjects: true })}
                        companyId={company.id}
                        forceSkipFetch={!company.id}
                        name="dealerId"
                        required
                    />
                </Col>
                <Col md={12} xs={24}>
                    <InputField
                        {...t('bmwModuleDetails:fields.sdmHost', { returnObjects: true })}
                        addonBefore="https://"
                        name="sdmHost"
                        required
                    />
                </Col>
                <Col md={12} xs={24}>
                    <InputField
                        {...t('bmwModuleDetails:fields.sdmAccessKey', { returnObjects: true })}
                        name="sdmAccessKey"
                        required
                    />
                </Col>
                <Col md={12} xs={24}>
                    <PasswordField
                        {...t('bmwModuleDetails:fields.sdmSecretKey', { returnObjects: true })}
                        name="sdmSecretKey"
                        placeholder="•••••"
                        required
                    />
                </Col>
            </Row>
        </>
    );
};

const implementation: BmwModuleImplementation = {
    PageComponent: ({ module }: BmwModulePageProps) => <BmwModulePage module={module} />,
    getTitle: t => t('moduleDetails:implementations.BmwModule.className'),
    FormComponent: Form,
    getValidator: () => formValidator,
    create: async (apolloClient, companyId, settings) =>
        apolloClient
            .mutate<CreateBmwModuleMutation, CreateBmwModuleMutationVariables>({
                mutation: CreateBmwModuleDocument,
                variables: { companyId, settings },
            })
            .then(({ data }) => data.module),
};

export default implementation;
